import React from 'react';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { FaArrowTrendUp, FaArrowTrendDown, FaCircle } from 'react-icons/fa6';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon,
  color,
  subtitle,
  trend
}) => {
  // Subtle background for icon
  const bgClass = (() => {
    try {
      if (typeof color === 'string' && color.startsWith('text-')) {
        return color.replace('text-', 'bg-') + ' bg-opacity-10';
      }
    } catch {
      // fallback: no bg class
    }
    return 'bg-white';
  })();

  // Get trend colors
  const getTrendColors = () => {
    switch (trend) {
      case 'up':
        return {
          bg: 'bg-emerald-50/70',
          text: 'text-emerald-700',
          icon: 'text-emerald-600',
          border: 'border-emerald-100'
        };
      case 'down':
        return {
          bg: 'bg-rose-50/70',
          text: 'text-rose-700',
          icon: 'text-rose-600',
          border: 'border-rose-100'
        };
      default:
        return {
          bg: 'bg-slate-50/70',
          text: 'text-slate-700',
          icon: 'text-slate-600',
          border: 'border-slate-100'
        };
    }
  };

  const trendColors = trend ? getTrendColors() : null;

  return (
    <div className="group">
      {/* Main Card - Reduced height with compact layout */}
      <div className="relative bg-white rounded-xl shadow-sm p-4 border border-gray-100/80 hover:border-gray-200 hover:shadow-xs transition-all duration-200 backdrop-blur-sm">
        
        {/* Header with icon and trend */}
        <div className="flex items-start justify-between mb-3">
          {/* Icon container - FIXED: Properly render the icon */}
          <div className={`p-2 rounded-lg ${bgClass} ${color} flex items-center justify-center`}>
            <div className="text-base">
              {icon}
            </div>
          </div>
          
          {trend && change && (
            <div className={`flex items-center gap-1 text-xs font-normal px-2 py-1 rounded-full border ${trendColors?.border} ${trendColors?.bg} ${trendColors?.text}`}>
              {trend === 'up' ? (
                <FaArrowTrendUp className="h-2.5 w-2.5" />
              ) : trend === 'down' ? (
                <FaArrowTrendDown className="h-2.5 w-2.5" />
              ) : (
                <FaCircle className="h-1.5 w-1.5" />
              )}
              <span className="text-xs">{change}</span>
            </div>
          )}
        </div>

        {/* Content - Compact spacing */}
        <div className="space-y-1">
          <p className="text-xs text-gray-500 font-medium tracking-wider uppercase">
            {title}
          </p>
          <p className="text-lg text-gray-900 font-medium">
            {typeof value === 'number' && !title.includes('Transactions') && !title.includes('Count')
              ? formatCurrency(value as number)
              : value}
          </p>
          
          {/* Subtle bottom text */}
          {subtitle && (
            <p className="text-xs text-gray-500 pt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatCard;